import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { useConfirm } from '../components/common/ConfirmDialog';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  Link,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  Checkbox,
  Collapse,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  CloudQueue as CloudIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Edit as EditIcon,
  Settings as SettingsIcon,
  TouchApp as TouchAppIcon,
  ArrowForward as ArrowForwardIcon,
  ContentCopy as ContentCopyIcon,
  OpenInNew as OpenInNewIcon,
  Search as SearchIcon,
  Group as GroupIcon,
  ContactPhone as ContactPhoneIcon,
  RocketLaunch as RocketLaunchIcon,
  AccountTree as AccountTreeIcon,
  ViewList as ListViewIcon,
  FolderOpen as FolderIcon,
  Business as BusinessIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  MoreVert as MoreVertIcon,
  CreateNewFolder as CreateNewFolderIcon,
  DriveFileMove as MoveIcon,
  DeleteOutline as DeleteOUIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import apiService from '../services/api';
import { isCustomerSubdomain, getCustomerIdFromSubdomain } from '../utils/constants';
import AccountRenameModal from '../components/AccountRenameModal';
import AccountOwnersModal from '../components/AccountOwnersModal';
import { filterInput } from '../utils/inputSanitization';
import { pageVariants, sectionVariants } from '../animations/variants';

// Org tree node for the Accounts hierarchy view with OU/account management actions
const AccountOrgTreeNode = ({ node, depth, parentId, onCreateOU, onDeleteOU, onMoveAccount }) => {
  const [expanded, setExpanded] = useState(true);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const hasChildren = node.children && node.children.length > 0;
  const isRoot = node.type === 'ROOT';
  const isOU = node.type === 'OU';
  const isAccount = node.type === 'ACCOUNT';
  const hasActions = isRoot || isOU || (isAccount && parentId);

  const getIcon = () => {
    switch (node.type) {
      case 'ROOT': return <BusinessIcon sx={{ fontSize: '1.1rem', color: '#4698D3' }} />;
      case 'OU': return <FolderIcon sx={{ fontSize: '1.1rem', color: '#F39C12' }} />;
      case 'ACCOUNT': return <CloudIcon sx={{ fontSize: '1.1rem', color: '#27AE60' }} />;
      default: return null;
    }
  };

  return (
    <Box sx={{ ml: depth > 0 ? 3 : 0 }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', py: 0.75, cursor: hasChildren ? 'pointer' : 'default', '&:hover': { bgcolor: '#F9FBFD' }, borderRadius: '6px', px: 0.5 }}
        onClick={() => hasChildren && setExpanded(!expanded)}
      >
        {hasChildren ? (
          <IconButton size="small" sx={{ mr: 0.5, p: 0.25 }}>
            {expanded ? <ExpandMoreIcon sx={{ fontSize: '1.1rem' }} /> : <ExpandLessIcon sx={{ fontSize: '1.1rem', transform: 'rotate(-90deg)' }} />}
          </IconButton>
        ) : (
          <Box sx={{ width: 28, mr: 0.5 }} />
        )}
        {getIcon()}
        <Typography sx={{ ml: 0.75, fontFamily: 'Metropolis, sans-serif', fontSize: '0.9rem', fontWeight: isAccount ? 400 : 500, color: '#253E66' }}>
          {node.name}
        </Typography>
        <Typography sx={{ ml: 1, fontFamily: 'monospace', fontSize: '0.75rem', color: '#999' }}>
          {node.id}
        </Typography>
        {!isAccount && (
          <Chip
            label={node.type}
            size="small"
            sx={{ ml: 0.5, fontSize: '0.6rem', height: 18, fontWeight: 600,
              bgcolor: isRoot ? 'rgba(70, 152, 211, 0.1)' : 'rgba(243, 156, 18, 0.1)',
              color: isRoot ? '#4698D3' : '#F39C12',
            }}
          />
        )}
        {node.status && (
          <Chip
            label={node.status}
            size="small"
            sx={{ ml: 0.5, fontSize: '0.6rem', height: 18, fontWeight: 600,
              bgcolor: node.status === 'ACTIVE' ? 'rgba(39, 174, 96, 0.1)' : 'rgba(231, 76, 60, 0.1)',
              color: node.status === 'ACTIVE' ? '#27AE60' : '#E74C3C',
            }}
          />
        )}
        {hasActions && (
          <Box sx={{ ml: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
              <MoreVertIcon sx={{ fontSize: '1.1rem', color: '#666' }} />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
              PaperProps={{ sx: { borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', minWidth: 200 } }}
            >
              {(isRoot || isOU) && (
                <MenuItem onClick={() => { onCreateOU(node); setMenuAnchor(null); }}>
                  <ListItemIcon><CreateNewFolderIcon sx={{ fontSize: '1.1rem', color: '#4698D3' }} /></ListItemIcon>
                  <ListItemText primaryTypographyProps={{ sx: { fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem' } }}>Create Organizational Unit</ListItemText>
                </MenuItem>
              )}
              {isOU && (
                <MenuItem onClick={() => { onDeleteOU(node); setMenuAnchor(null); }}>
                  <ListItemIcon><DeleteOUIcon sx={{ fontSize: '1.1rem', color: '#E74C3C' }} /></ListItemIcon>
                  <ListItemText primaryTypographyProps={{ sx: { fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem' } }}>Delete OU</ListItemText>
                </MenuItem>
              )}
              {isAccount && parentId && (
                <MenuItem onClick={() => { onMoveAccount(node, parentId); setMenuAnchor(null); }}>
                  <ListItemIcon><MoveIcon sx={{ fontSize: '1.1rem', color: '#F39C12' }} /></ListItemIcon>
                  <ListItemText primaryTypographyProps={{ sx: { fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem' } }}>Move Account</ListItemText>
                </MenuItem>
              )}
            </Menu>
          </Box>
        )}
      </Box>
      {hasChildren && (
        <Collapse in={expanded}>
          {node.children.map((child) => (
            <AccountOrgTreeNode key={child.id} node={child} depth={depth + 1} parentId={node.id} onCreateOU={onCreateOU} onDeleteOU={onDeleteOU} onMoveAccount={onMoveAccount} />
          ))}
        </Collapse>
      )}
    </Box>
  );
};

const ManualOnboarding = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [onboardedAccounts, setOnboardedAccounts] = useState([]);
  const [searchQuery, setSearchQuery] = useState(''); // Display value
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(''); // Value sent to API
  const searchTimeoutRef = useRef(null);

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 25,
    total: 0,
    totalPages: 0,
  });

  // Account form state
  const [accounts, setAccounts] = useState([{ accountId: '', accountName: '' }]);
  const [dryRun, setDryRun] = useState(false);

  // Onboarding method state
  const [onboardingMethod, setOnboardingMethod] = useState(null); // 'automated' or 'manual'
  const [onboardingConfig, setOnboardingConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [copiedPolicy, setCopiedPolicy] = useState(false);
  const [copiedRoleName, setCopiedRoleName] = useState(false);

  // Validation state
  const [validating, setValidating] = useState(false);
  const [validated, setValidated] = useState(false);
  const [validationError, setValidationError] = useState(null);

  // Add deboard state
  const [deboardingAccount, setDeboardingAccount] = useState(null);

  // Customer context
  const isCustomer = isCustomerSubdomain();
  const customerId = getCustomerIdFromSubdomain();

  // Add state for assignment checking
  const [checkingAssignments, setCheckingAssignments] = useState({});

  // Add state for rename modal
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renamingAccount, setRenamingAccount] = useState(null);

  // Add state for owners modal
  const [showOwnersModal, setShowOwnersModal] = useState(false);
  const [ownersAccount, setOwnersAccount] = useState(null);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [isBulkOwnersMode, setIsBulkOwnersMode] = useState(false);

  // Users list for owners modal
  const [users, setUsers] = useState([]);

  // Org tree management dialogs
  const [createOUOpen, setCreateOUOpen] = useState(false);
  const [createOUParent, setCreateOUParent] = useState(null);
  const [newOUName, setNewOUName] = useState('');
  const [deleteOUOpen, setDeleteOUOpen] = useState(false);
  const [deleteOUTarget, setDeleteOUTarget] = useState(null);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveAccountNode, setMoveAccountNode] = useState(null);
  const [moveSourceParent, setMoveSourceParent] = useState('');
  const [moveDestParent, setMoveDestParent] = useState('');
  const [orgActionLoading, setOrgActionLoading] = useState(false);

  // Billing status state
  const [billingStatuses, setBillingStatuses] = useState({});
  const [onboardingToBilling, setOnboardingToBilling] = useState(null);
  const [selfServeEnabled, setSelfServeEnabled] = useState(false);

  // Org tree view state
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'hierarchy'
  const [orgEnabled, setOrgEnabled] = useState(false);
  const [orgData, setOrgData] = useState(null);
  const [orgLoading, setOrgLoading] = useState(false);
  const [managementAccountId, setManagementAccountId] = useState('');

  // Handle search with debounce
  const handleSearchChange = useCallback((value) => {
    setSearchQuery(value); // Update display value immediately

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchQuery(value); // Update API search value after debounce
      setPagination(prev => ({ ...prev, page: 0 })); // Reset to first page on search
    }, 300);
  }, []);

  // Fetch accounts with pagination
  const fetchOnboardedAccountsPaginated = useCallback(async (isInitial = false) => {
    if (isInitial) {
      setInitialLoading(true);
    } else {
      setRefreshing(true);
    }
    try {
      const response = await apiService.getAwsAccountsFromDB({
        page: pagination.page + 1, // API uses 1-indexed pages
        pageSize: pagination.pageSize,
        search: debouncedSearchQuery || undefined,
      });
      if (response && response.success && response.data) {
        const accounts = response.data.accounts || [];
        const formattedAccounts = accounts.map(account => ({
          accountId: account.accountId || account.account_id,
          accountName: account.accountName || account.name || `Account-${account.accountId || account.account_id}`,
          status: account.status || 'Active',
          owner_emails: account.owner_emails || account.ownerEmails || [],
        }));
        setOnboardedAccounts(formattedAccounts);
        setPagination(prev => ({
          ...prev,
          total: response.data.total || 0,
          totalPages: response.data.totalPages || 0,
        }));

        // Fetch billing status for the accounts on this page
        const accountIds = formattedAccounts.map(a => a.accountId).filter(Boolean);
        if (accountIds.length > 0) {
          try {
            const billingResponse = await apiService.checkBillingStatus(accountIds);
            if (billingResponse.statuses) {
              setBillingStatuses(billingResponse.statuses);
            }
          } catch (billingError) {
            console.error('Error fetching billing status:', billingError);
          }
        }
      } else {
        setOnboardedAccounts([]);
        setPagination(prev => ({ ...prev, total: 0, totalPages: 0 }));
      }
    } catch (error) {
      console.error('Error fetching onboarded accounts:', error);
      if (!error.message.includes('404') && !error.message.includes('not found')) {
        toast.error('Failed to fetch onboarded accounts');
      }
      setOnboardedAccounts([]);
      setPagination(prev => ({ ...prev, total: 0, totalPages: 0 }));
    } finally {
      setInitialLoading(false);
      setRefreshing(false);
    }
  }, [pagination.page, pagination.pageSize, debouncedSearchQuery]);

  // Initial fetch and refetch when pagination/search changes
  useEffect(() => {
    fetchOnboardedAccountsPaginated(pagination.page === 0 && !debouncedSearchQuery);
  }, [fetchOnboardedAccountsPaginated]);

  // Fetch customer config for feature flags
  useEffect(() => {
    apiService.getCurrentCustomer().then(res => {
      const customer = res?.data?.customer;
      if (customer?.enableSelfServeOnboarding) {
        setSelfServeEnabled(true);
      }
      if (customer?.enableOrgServices && customer?.managementAccountId) {
        setOrgEnabled(true);
        setManagementAccountId(customer.managementAccountId);
      }
    }).catch(() => {});
  }, []);

  // Fetch org tree when hierarchy view is selected
  useEffect(() => {
    if (viewMode === 'hierarchy' && managementAccountId && !orgData) {
      setOrgLoading(true);
      apiService.getOrgStructure(managementAccountId).then(data => {
        setOrgData(data);
      }).catch(err => {
        console.error('Failed to fetch org structure:', err);
      }).finally(() => {
        setOrgLoading(false);
      });
    }
  }, [viewMode, managementAccountId, orgData]);

  // Org tree management helpers
  const collectOUs = (n, list = []) => {
    if (n.type === 'ROOT' || n.type === 'OU') list.push({ id: n.id, name: n.name, type: n.type });
    if (n.children) n.children.forEach((c) => collectOUs(c, list));
    return list;
  };
  const allOUs = orgData?.tree ? orgData.tree.flatMap((n) => collectOUs(n)) : [];

  const refreshOrgTree = async () => {
    if (!managementAccountId) return;
    setOrgLoading(true);
    try {
      const data = await apiService.getOrgStructure(managementAccountId);
      setOrgData(data);
    } catch (err) {
      console.error('Failed to refresh org tree:', err);
    } finally {
      setOrgLoading(false);
    }
  };

  const handleCreateOUOpen = (parentNode) => { setCreateOUParent(parentNode); setNewOUName(''); setCreateOUOpen(true); };
  const handleCreateOU = async () => {
    if (!newOUName.trim() || !createOUParent) return;
    setOrgActionLoading(true);
    try {
      await apiService.createOU(managementAccountId, createOUParent.id, newOUName.trim());
      setCreateOUOpen(false);
      toast.success('OU created successfully');
      await refreshOrgTree();
    } catch (err) { toast.error(err.message || 'Failed to create OU'); }
    finally { setOrgActionLoading(false); }
  };

  const handleDeleteOUOpen = (ouNode) => { setDeleteOUTarget(ouNode); setDeleteOUOpen(true); };
  const handleDeleteOU = async () => {
    if (!deleteOUTarget) return;
    setOrgActionLoading(true);
    try {
      await apiService.deleteOU(managementAccountId, deleteOUTarget.id);
      setDeleteOUOpen(false);
      toast.success('OU deleted successfully');
      await refreshOrgTree();
    } catch (err) { toast.error(err.message || 'Failed to delete OU'); }
    finally { setOrgActionLoading(false); }
  };

  const handleMoveOpen = (accountNode, parentId) => { setMoveAccountNode(accountNode); setMoveSourceParent(parentId); setMoveDestParent(''); setMoveOpen(true); };
  const handleMoveAccount = async () => {
    if (!moveAccountNode || !moveDestParent) return;
    setOrgActionLoading(true);
    try {
      await apiService.moveAccount(managementAccountId, moveAccountNode.id, moveSourceParent, moveDestParent);
      setMoveOpen(false);
      toast.success('Account moved successfully');
      await refreshOrgTree();
    } catch (err) { toast.error(err.message || 'Failed to move account'); }
    finally { setOrgActionLoading(false); }
  };

  // Fetch onboarding configuration when modal opens
  useEffect(() => {
    if (showOnboardingModal && !onboardingConfig) {
      fetchOnboardingConfig();
    }
  }, [showOnboardingModal]);

  const fetchOnboardingConfig = async () => {
    setLoadingConfig(true);
    try {
      const response = await apiService.getOnboardingConfig();
      if (response && response.success && response.data) {
        setOnboardingConfig(response.data);
      }
    } catch (error) {
      console.error('Error fetching onboarding config:', error);
      toast.error('Failed to fetch onboarding configuration');
    } finally {
      setLoadingConfig(false);
    }
  };

  // Generate CloudFormation URL
  const getCloudFormationUrl = () => {
    if (!onboardingConfig) return '#';
    const externalId = onboardingConfig.externalId || customerId || '';
    const prismAccountId = onboardingConfig.prismAccountId || '';
    return `https://us-east-1.console.aws.amazon.com/cloudformation/home?region=us-east-1#/stacks/quickcreate?templateURL=https://ck-prism-cfn.s3.us-east-1.amazonaws.com/prism-cfn.yml&stackName=ck-prism&param_ExternalIdParameter=${externalId}&param_PrismAccountId=${prismAccountId}`;
  };

  // Get the trust policy for manual setup
  const getTrustPolicy = () => {
    const prismAccountId = onboardingConfig?.prismAccountId || '<LOADING...>';
    const externalId = onboardingConfig?.externalId || customerId || '<LOADING...>';
    return JSON.stringify({
      "Version": "2012-10-17",
      "Statement": [
        {
          "Effect": "Allow",
          "Principal": {
            "AWS": `arn:aws:iam::${prismAccountId}:root`
          },
          "Action": "sts:AssumeRole",
          "Condition": {
            "StringEquals": {
              "sts:ExternalId": externalId
            }
          }
        }
      ]
    }, null, 2);
  };

  const handleCopyTrustPolicy = () => {
    navigator.clipboard.writeText(getTrustPolicy());
    setCopiedPolicy(true);
    setTimeout(() => setCopiedPolicy(false), 2000);
    toast.success('Trust policy copied to clipboard');
  };

  const handleCopyRoleName = () => {
    const roleName = onboardingConfig?.switchRoleName || 'ckprism-role';
    navigator.clipboard.writeText(roleName);
    setCopiedRoleName(true);
    setTimeout(() => setCopiedRoleName(false), 2000);
    toast.success('Role name copied to clipboard');
  };

  const handleOpenModal = () => {
    setShowOnboardingModal(true);
    setOnboardingMethod(null);
    setAccounts([{ accountId: '', accountName: '' }]);
    setValidated(false);
    setValidating(false);
    setValidationError(null);
  };

  const handleCloseModal = () => {
    setShowOnboardingModal(false);
    setOnboardingMethod(null);
    setAccounts([{ accountId: '', accountName: '' }]);
    setValidated(false);
    setValidating(false);
    setValidationError(null);
  };

  const handleBack = () => {
    setOnboardingMethod(null);
    setAccounts([{ accountId: '', accountName: '' }]);
    setValidated(false);
    setValidating(false);
    setValidationError(null);
  };

  const handleValidateAccount = async () => {
    const accountId = accounts[0].accountId;
    if (!accountId || !validateAccountId(accountId)) {
      toast.error('Please enter a valid 12-digit AWS Account ID');
      return;
    }

    setValidating(true);
    setValidationError(null);
    setValidated(false);

    try {
      const response = await apiService.validateAccountAccess(accountId);
      if (response && response.success) {
        setValidated(true);
        toast.success('Account access validated successfully!');
      } else {
        setValidationError('Please ensure the IAM role is correctly configured with the proper trust policy and permissions.');
        toast.error('Validation failed');
      }
    } catch (error) {
      setValidationError('Please ensure the IAM role is correctly configured with the proper trust policy and permissions.');
      toast.error('Validation failed');
    } finally {
      setValidating(false);
    }
  };

  // Wrapper function for refresh and other calls
  const fetchOnboardedAccounts = () => {
    fetchOnboardedAccountsPaginated(false);
  };

  const validateAccountId = (accountId) => {
    return /^\d{12}$/.test(accountId);
  };

  const handleAccountChange = (index, field, value) => {
    const updatedAccounts = [...accounts];
    updatedAccounts[index][field] = value;
    setAccounts(updatedAccounts);
    // Reset validation when account ID changes
    if (field === 'accountId') {
      setValidated(false);
      setValidationError(null);
    }
  };

  const addAccount = () => {
    setAccounts([...accounts, { accountId: '', accountName: '' }]);
  };

  const removeAccount = (index) => {
    if (accounts.length > 1) {
      setAccounts(accounts.filter((_, i) => i !== index));
    }
  };

  const handleOnboardingSubmit = async () => {
    for (let i = 0; i < accounts.length; i++) {
      const account = accounts[i];
      if (!account.accountId) {
        toast.error(`Account ID is required for account ${i + 1}`);
        return;
      }
      if (!validateAccountId(account.accountId)) {
        toast.error(`Account ID must be exactly 12 digits for account ${i + 1}`);
        return;
      }
    }

    setLoading(true);

    try {
      const accountsWithDefaults = accounts.map(account => ({
        accountId: account.accountId,
        accountName: account.accountName || `Account-${account.accountId}`
      }));

      let response;
      if (accounts.length === 1) {
        response = await apiService.manualOnboardSingleAccount({
          ...accountsWithDefaults[0],
          dryRun
        });
      } else {
        response = await apiService.manualOnboardBulkAccounts({
          accounts: accountsWithDefaults,
          dryRun
        });
      }

      if (response.success) {
        toast.success(response.message);
        setShowOnboardingModal(false);
        setAccounts([{ accountId: '', accountName: '' }]);
        setDryRun(false);
        fetchOnboardedAccounts();
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeboardAccount = async (accountId) => {
    const confirmed = await confirm({
      title: 'Deboard AWS Account',
      message: `Are you sure you want to deboard AWS account ${accountId}? This action cannot be undone.`,
      confirmText: 'Deboard',
      variant: 'danger',
    });
    if (!confirmed) {
      return;
    }

    setCheckingAssignments(prev => ({ ...prev, [accountId]: true }));

    try {
      const assignmentsResponse = await apiService.checkAWSAccountAssignments(accountId);

      if (assignmentsResponse && assignmentsResponse.success && assignmentsResponse.data) {
        const assignments = assignmentsResponse.data.assignments || [];

        if (assignments.length > 0) {
          toast.error(`Cannot deboard account: ${assignments.length} active assignment(s) must be removed first`);
          setCheckingAssignments(prev => ({ ...prev, [accountId]: false }));
          return;
        }
      }

      setDeboardingAccount(accountId);
      const response = await apiService.deboardAWSAccount(accountId);

      if (response && response.success) {
        toast.success(`AWS account ${accountId} has been successfully deboarded`);
        await fetchOnboardedAccounts();
      } else {
        throw new Error(response?.message || 'Failed to deboard account');
      }
    } catch (error) {
      console.error('Deboard error:', error);
      let errorMessage = error.message || 'Failed to deboard AWS account';
      toast.error(errorMessage);
    } finally {
      setDeboardingAccount(null);
      setCheckingAssignments(prev => ({ ...prev, [accountId]: false }));
    }
  };

  const canDeboardAccount = (accountId) => {
    return !deboardingAccount && !checkingAssignments[accountId];
  };

  const pollOnboardResult = async (accountId) => {
    const pollInterval = 5000;
    const maxAttempts = 40;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise(resolve => setTimeout(resolve, pollInterval));
      try {
        const status = await apiService.pollOnboardStatus(accountId);
        if (status.status === 'success') {
          setBillingStatuses(prev => ({ ...prev, [accountId]: true }));
          toast.success(status.message || 'Account onboarded to CK Billing successfully');
          return 'success';
        }
        if (status.status === 'error') {
          // Management account org conflict — prompt user to delete org
          if (status.error_code === 'org_conflict' && status.error_meta?.is_management_account) {
            const meta = status.error_meta;

            if (meta.member_account_count > 0) {
              // Org has members — user must remove them manually first
              await confirm({
                title: 'Organization Has Members',
                message: `Account ${accountId} is the management account of an AWS Organization (${meta.org_id}) that has ${meta.member_account_count} member account(s). Please remove all member accounts from the organization first, then try again.`,
                confirmText: 'OK',
                variant: 'warning',
                hideCancel: true,
              });
              return 'error';
            }

            // Org has no members — offer to delete it
            const approved = await confirm({
              title: 'Delete Organization & Onboard',
              message: `Account ${accountId} is the management account of an empty AWS Organization (${meta.org_id}). The organization must be deleted before this account can be onboarded to CK Billing. Do you want to delete it and proceed?`,
              confirmText: 'Delete Organization & Onboard',
              variant: 'danger',
            });
            if (!approved) return 'cancelled';

            try {
              toast.info('Deleting organization, this may take a moment...');
              await apiService.leaveOrganization(accountId);
              toast.success('Organization deleted. Retrying onboard...');
              return 'retry';
            } catch (leaveErr) {
              toast.error(leaveErr.message || 'Failed to delete organization');
              return 'error';
            }
          }
          toast.error(status.error || 'Failed to onboard account');
          return 'error';
        }
      } catch {
        break;
      }
    }
    toast.error('Onboarding is taking longer than expected. Please check back later.');
    return 'timeout';
  };

  const handleSelfOnboardToBilling = async (accountId) => {
    const confirmed = await confirm({
      title: 'Onboard to CK Billing',
      message: `Are you sure you want to onboard account ${accountId} to CloudKeeper Billing? This will move the account into your organization's billing.`,
      confirmText: 'Onboard',
      variant: 'warning',
    });
    if (!confirmed) return;

    try {
      setOnboardingToBilling(accountId);

      // Allow retry loop for org conflict resolution
      for (let attempt = 0; attempt < 3; attempt++) {
        const response = await apiService.selfOnboardToBilling(accountId);

        if (response.status === 'success' || response.status === 'already_in_billing') {
          setBillingStatuses(prev => ({ ...prev, [accountId]: true }));
          toast.success(response.message || 'Account onboarded to CK Billing successfully');
          return;
        }

        if (response.status === 'pending') {
          const result = await pollOnboardResult(accountId);
          if (result === 'success') return;
          if (result === 'retry') continue; // org was deleted, retry onboard
          return; // error, cancelled, or timeout
        }

        toast.error(response.error || response.message || 'Failed to onboard account');
        return;
      }
      toast.error('Failed to onboard account after multiple attempts');
    } catch (error) {
      toast.error(error.message || 'Failed to onboard account to CK Billing');
    } finally {
      setOnboardingToBilling(null);
    }
  };

  const handleRenameClick = (account) => {
    setRenamingAccount(account);
    setShowRenameModal(true);
  };

  const handleRenameSuccess = () => {
    fetchOnboardedAccounts();
  };

  // Handlers for owners modal
  const handleEditOwnersClick = async (account) => {
    setOwnersAccount(account);
    setShowOwnersModal(true);
    // Fetch users if not already loaded
    if (users.length === 0) {
      try {
        const response = await apiService.getAllUsers({ page: 1, pageSize: 1000 });
        if (response && response.success && response.data) {
          setUsers(response.data.users || []);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    }
  };

  const handleOwnersSuccess = () => {
    fetchOnboardedAccounts();
    setSelectedAccounts([]);
    setIsBulkOwnersMode(false);
  };

  // Selection handlers
  const handleSelectAccount = (accountId) => {
    setSelectedAccounts((prev) => {
      if (prev.includes(accountId)) {
        return prev.filter((id) => id !== accountId);
      } else {
        return [...prev, accountId];
      }
    });
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allAccountIds = displayedAccounts.map((account) => account.accountId);
      setSelectedAccounts(allAccountIds);
    } else {
      setSelectedAccounts([]);
    }
  };

  // Bulk owners handler
  const handleBulkSetOwners = async () => {
    if (selectedAccounts.length === 0) return;

    setIsBulkOwnersMode(true);
    // Create a virtual account object for bulk mode
    const bulkAccount = {
      accountId: 'bulk',
      accountName: `${selectedAccounts.length} accounts selected`,
      owner_emails: [],
    };
    setOwnersAccount(bulkAccount);
    setShowOwnersModal(true);

    // Fetch users if not already loaded
    if (users.length === 0) {
      try {
        const response = await apiService.getAllUsers({ page: 1, pageSize: 1000 });
        if (response && response.success && response.data) {
          setUsers(response.data.users || []);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    }
  };

  // Minimalist input style
  const inputStyle = {
    '& .MuiOutlinedInput-root': {
      fontFamily: 'Metropolis, sans-serif',
      backgroundColor: '#FFFFFF',
      '& fieldset': {
        borderColor: '#E5E5E5',
      },
      '&:hover fieldset': {
        borderColor: '#F39C12',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#F39C12',
      },
    },
    '& .MuiInputLabel-root': {
      fontFamily: 'Metropolis, sans-serif',
      color: '#666666',
      '&.Mui-focused': {
        color: '#F39C12',
      },
    },
    '& .MuiFormHelperText-root': {
      fontFamily: 'Metropolis, sans-serif',
    },
  };

  // Use server-side filtered results directly
  const displayedAccounts = onboardedAccounts;

  // Selection state computations
  const isAllSelected = displayedAccounts.length > 0 &&
    displayedAccounts.every((account) => selectedAccounts.includes(account.accountId));

  const isIndeterminate = selectedAccounts.length > 0 && !isAllSelected;

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      style={{
        minHeight: '100vh',
        paddingTop: 32,
        paddingBottom: 32,
        paddingLeft: 32,
        paddingRight: 32,
        backgroundColor: '#F9FBFD',
      }}
    >
      {/* Page Header */}
      <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          border: '1px solid #E5E5E5',
          p: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                backgroundColor: '#F39C12',
                width: 48,
                height: 48,
              }}
            >
              <CloudIcon sx={{ fontSize: 24 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: 'Bitter, Georgia, serif',
                  fontWeight: 600,
                  fontSize: '1.75rem',
                  color: '#253E66',
                }}
              >
                AWS Accounts
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.875rem',
                  color: '#666666',
                  fontWeight: 500,
                }}
              >
                {pagination.total} account{pagination.total !== 1 ? 's' : ''} onboarded
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            {selectedAccounts.length > 0 && (
              <Button
                variant="contained"
                startIcon={<GroupIcon />}
                onClick={handleBulkSetOwners}
                sx={{
                  backgroundColor: '#4698D3',
                  borderRadius: '6px',
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 2.5,
                  '&:hover': {
                    backgroundColor: '#3580B8',
                  },
                }}
              >
                Set Owners ({selectedAccounts.length})
              </Button>
            )}
            <TextField
              placeholder="Search by name, ID, or JIT owner..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              size="small"
              sx={{
                width: 320,
                '& .MuiOutlinedInput-root': {
                  fontFamily: 'Metropolis, sans-serif',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '6px',
                  '& fieldset': { borderColor: '#E5E5E5' },
                  '&:hover fieldset': { borderColor: '#F39C12' },
                  '&.Mui-focused fieldset': { borderColor: '#F39C12' },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#999999', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title={viewMode === 'hierarchy' ? 'Refresh Org Tree' : 'Refresh Accounts'}>
              <IconButton
                onClick={() => (viewMode === 'hierarchy' ? refreshOrgTree() : fetchOnboardedAccounts())}
                disabled={refreshing || (viewMode === 'hierarchy' && orgLoading)}
                sx={{
                  color: '#666666',
                  backgroundColor: '#F9FBFD',
                  border: '1px solid #E5E5E5',
                  borderRadius: '6px',
                  '&:hover': {
                    backgroundColor: '#F0F4F8',
                    borderColor: '#F39C12',
                    color: '#F39C12',
                  },
                  '&:disabled': {
                    color: '#BDBDBD',
                    backgroundColor: '#F9FBFD',
                  },
                }}
              >
                {(refreshing || (viewMode === 'hierarchy' && orgLoading)) ? (
                  <CircularProgress size={20} sx={{ color: '#F39C12' }} />
                ) : (
                  <RefreshIcon />
                )}
              </IconButton>
            </Tooltip>

            {orgEnabled && (
              <Box sx={{ display: 'flex', border: '1px solid #E5E5E5', borderRadius: '6px', overflow: 'hidden' }}>
                <Button
                  size="small"
                  startIcon={<AccountTreeIcon sx={{ fontSize: '1rem' }} />}
                  onClick={() => setViewMode('hierarchy')}
                  sx={{
                    textTransform: 'none', fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '0.8rem',
                    px: 1.5, py: 0.5, borderRadius: 0, minWidth: 'auto',
                    backgroundColor: viewMode === 'hierarchy' ? '#4698D3' : 'transparent',
                    color: viewMode === 'hierarchy' ? '#fff' : '#666',
                    '&:hover': { backgroundColor: viewMode === 'hierarchy' ? '#3580B8' : 'rgba(70, 152, 211, 0.08)' },
                  }}
                >
                  Hierarchy
                </Button>
                <Button
                  size="small"
                  startIcon={<ListViewIcon sx={{ fontSize: '1rem' }} />}
                  onClick={() => setViewMode('list')}
                  sx={{
                    textTransform: 'none', fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '0.8rem',
                    px: 1.5, py: 0.5, borderRadius: 0, borderLeft: '1px solid #E5E5E5', minWidth: 'auto',
                    backgroundColor: viewMode === 'list' ? '#4698D3' : 'transparent',
                    color: viewMode === 'list' ? '#fff' : '#666',
                    '&:hover': { backgroundColor: viewMode === 'list' ? '#3580B8' : 'rgba(70, 152, 211, 0.08)' },
                  }}
                >
                  List
                </Button>
              </Box>
            )}

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenModal}
              sx={{
                backgroundColor: '#F39C12',
                borderRadius: '6px',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                px: 3,
                '&:hover': {
                  backgroundColor: '#E67E22',
                },
              }}
            >
              Onboard Account
            </Button>
          </Box>
        </Box>
      </Paper>
      </motion.div>

      {/* Accounts Grid */}
      <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #E5E5E5',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        {viewMode === 'hierarchy' ? (
          <Box sx={{ p: 3 }}>
            {orgLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, py: 1.5, mb: 1, bgcolor: 'rgba(243, 156, 18, 0.05)', borderRadius: '6px' }}>
                <CircularProgress size={16} sx={{ color: '#F39C12' }} />
                <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#F39C12', fontWeight: 500 }}>
                  Refreshing organization tree...
                </Typography>
              </Box>
            )}
            {orgData?.tree ? (
              <Box sx={{ opacity: orgLoading ? 0.5 : 1, pointerEvents: orgLoading ? 'none' : 'auto', transition: 'opacity 0.2s' }}>
                {orgData.tree.map((rootNode) => (
                  <AccountOrgTreeNode key={rootNode.id} node={{ ...rootNode, name: 'Root' }} depth={0} parentId={null} onCreateOU={handleCreateOUOpen} onDeleteOU={handleDeleteOUOpen} onMoveAccount={handleMoveOpen} />
                ))}
              </Box>
            ) : !orgLoading ? (
              <Typography sx={{ color: '#999', textAlign: 'center', py: 4, fontFamily: 'Metropolis, sans-serif' }}>
                Failed to load organization structure.
              </Typography>
            ) : null}
          </Box>
        ) : initialLoading ? (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '300px',
            gap: 2,
            py: 6,
          }}>
            <CircularProgress sx={{ color: '#F39C12' }} />
            <Typography
              sx={{
                color: '#666666',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 500,
                fontSize: '0.875rem',
              }}
            >
              Loading accounts...
            </Typography>
          </Box>
        ) : onboardedAccounts.length === 0 ? (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '300px',
            gap: 2,
            py: 6,
          }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                backgroundColor: '#FEF9E7',
              }}
            >
              <CloudIcon sx={{ fontSize: 32, color: '#F39C12' }} />
            </Avatar>
            <Typography
              sx={{
                color: '#253E66',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                fontSize: '1.1rem',
                textAlign: 'center',
              }}
            >
              No AWS accounts onboarded
            </Typography>
            <Typography
              sx={{
                color: '#666666',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 500,
                fontSize: '0.875rem',
                textAlign: 'center',
                maxWidth: 300,
              }}
            >
              Onboard your first AWS account to get started with permission assignments
            </Typography>
          </Box>
        ) : (
          <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#F9FBFD' }}>
                  <TableCell padding="checkbox" sx={{ borderBottom: '1px solid #E5E5E5', width: 50 }}>
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={isIndeterminate}
                      onChange={handleSelectAll}
                      sx={{
                        color: '#E5E5E5',
                        '&.Mui-checked': {
                          color: '#F39C12',
                        },
                        '&.MuiCheckbox-indeterminate': {
                          color: '#F39C12',
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: '#253E66',
                      borderBottom: '1px solid #E5E5E5',
                    }}
                  >
                    Account Name
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: '#253E66',
                      borderBottom: '1px solid #E5E5E5',
                    }}
                  >
                    Account ID
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: '#253E66',
                      borderBottom: '1px solid #E5E5E5',
                      width: 140,
                    }}
                  >
                    Billing Status
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: '#253E66',
                      borderBottom: '1px solid #E5E5E5',
                      width: 220,
                    }}
                  >
                    JIT Owners
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: '#253E66',
                      borderBottom: '1px solid #E5E5E5',
                      width: 100,
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedAccounts.map((account, index) => (
                  <TableRow
                    key={account.accountId}
                    sx={{
                      '@keyframes fadeInRow': {
                        from: { opacity: 0, transform: 'translateY(8px)' },
                        to: { opacity: 1, transform: 'translateY(0)' },
                      },
                      animation: `fadeInRow 0.3s ease-out ${index * 0.05}s both`,
                      '&:hover': {
                        backgroundColor: '#F9FBFD',
                      },
                      '&:last-child td': {
                        borderBottom: 'none',
                      },
                    }}
                  >
                    <TableCell padding="checkbox" sx={{ borderBottom: '1px solid #E5E5E5' }}>
                      <Checkbox
                        checked={selectedAccounts.includes(account.accountId)}
                        onChange={() => handleSelectAccount(account.accountId)}
                        sx={{
                          color: '#E5E5E5',
                          '&.Mui-checked': {
                            color: '#F39C12',
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #E5E5E5' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          sx={{
                            backgroundColor: '#FEF9E7',
                            width: 36,
                            height: 36,
                          }}
                        >
                          <CloudIcon sx={{ fontSize: 18, color: '#F39C12' }} />
                        </Avatar>
                        <Typography
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            color: '#253E66',
                          }}
                        >
                          {account.accountName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #E5E5E5' }}>
                      <Typography
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.875rem',
                          color: '#253E66',
                        }}
                      >
                        {account.accountId}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #E5E5E5' }}>
                      {billingStatuses[account.accountId] === true ? (
                        <Chip
                          label="In CK Billing"
                          size="small"
                          sx={{
                            backgroundColor: '#E8F5E9',
                            color: '#2E7D32',
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                          }}
                        />
                      ) : billingStatuses[account.accountId] === false ? (
                        selfServeEnabled ? (
                          <Button
                            size="small"
                            variant="outlined"
                            disableElevation
                            disabled={onboardingToBilling === account.accountId}
                            onClick={() => handleSelfOnboardToBilling(account.accountId)}
                            startIcon={
                              onboardingToBilling === account.accountId
                                ? <CircularProgress size={12} sx={{ color: '#E65100' }} />
                                : <RocketLaunchIcon sx={{ fontSize: '0.9rem' }} />
                            }
                            sx={{
                              textTransform: 'none',
                              fontFamily: 'Metropolis, sans-serif',
                              fontWeight: 600,
                              fontSize: '0.72rem',
                              color: '#E65100',
                              backgroundColor: '#FFF8F0',
                              borderColor: '#FFB74D',
                              borderRadius: '6px',
                              py: 0.4,
                              px: 1.25,
                              whiteSpace: 'nowrap',
                              '&:hover': {
                                backgroundColor: '#FFF3E0',
                                borderColor: '#E65100',
                              },
                              '&.Mui-disabled': {
                                color: '#E65100',
                                borderColor: '#FFB74D',
                                opacity: 0.7,
                              },
                              '& .MuiButton-startIcon': { mr: 0.5 },
                            }}
                          >
                            {onboardingToBilling === account.accountId ? 'Onboarding…' : 'Onboard to CK Billing'}
                          </Button>
                        ) : (
                          <Chip
                            label="Not in CK Billing"
                            size="small"
                            sx={{
                              backgroundColor: '#FFF3E0',
                              color: '#E65100',
                              fontFamily: 'Metropolis, sans-serif',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                            }}
                          />
                        )
                      ) : (
                        <Typography
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            color: '#999999',
                            fontSize: '0.875rem',
                          }}
                        >
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #E5E5E5' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {account.owner_emails && account.owner_emails.length > 0 ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
                            {account.owner_emails.slice(0, 2).map((email, idx) => (
                              <Chip
                                key={idx}
                                label={email}
                                size="small"
                                sx={{
                                  fontFamily: 'Metropolis, sans-serif',
                                  fontSize: '0.75rem',
                                  height: 22,
                                  backgroundColor: '#EBF5FB',
                                  color: '#253E66',
                                  border: '1px solid #4698D320',
                                  maxWidth: 150,
                                  '& .MuiChip-label': {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  },
                                }}
                              />
                            ))}
                            {account.owner_emails.length > 2 && (
                              <Typography
                                sx={{
                                  fontFamily: 'Metropolis, sans-serif',
                                  color: '#666666',
                                  fontSize: '0.75rem',
                                  fontWeight: 500,
                                }}
                              >
                                +{account.owner_emails.length - 2} more
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography
                            sx={{
                              fontFamily: 'Metropolis, sans-serif',
                              color: '#999999',
                              fontSize: '0.8rem',
                              fontStyle: 'italic',
                              flex: 1,
                            }}
                          >
                            No owners
                          </Typography>
                        )}
                        <Tooltip title="Edit owners">
                          <IconButton
                            onClick={() => handleEditOwnersClick(account)}
                            size="small"
                            sx={{
                              color: '#4698D3',
                              '&:hover': { backgroundColor: '#EBF5FB' },
                            }}
                          >
                            <GroupIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #E5E5E5' }}>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title={billingStatuses[account.accountId] === true ? 'Manage contacts' : 'Account must be in CK Billing to manage contacts'}>
                          <span>
                            <IconButton
                              onClick={() => navigate(`/accounts/${account.accountId}/contacts`)}
                              size="small"
                              disabled={billingStatuses[account.accountId] !== true}
                              sx={{
                                color: billingStatuses[account.accountId] === true ? '#4698D3' : '#ccc',
                                '&:hover': { backgroundColor: '#EBF5FB' },
                              }}
                            >
                              <ContactPhoneIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Rename account">
                          <IconButton
                            onClick={() => handleRenameClick(account)}
                            size="small"
                            sx={{
                              color: '#4698D3',
                              '&:hover': { backgroundColor: '#EBF5FB' },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={!canDeboardAccount(account.accountId) ? "Checking assignments..." : "Deboard account"}>
                          <span>
                            <IconButton
                              onClick={() => handleDeboardAccount(account.accountId)}
                              disabled={!canDeboardAccount(account.accountId)}
                              size="small"
                              sx={{
                                color: '#E74C3C',
                                '&:hover': { backgroundColor: '#FDF2F2' },
                                '&:disabled': { color: '#E74C3C50' },
                              }}
                            >
                              {checkingAssignments[account.accountId] || deboardingAccount === account.accountId ? (
                                <CircularProgress size={16} sx={{ color: '#E74C3C' }} />
                              ) : (
                                <DeleteIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* No results for search */}
          {displayedAccounts.length === 0 && searchQuery && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
              }}
            >
              <Typography
                sx={{
                  color: '#666666',
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 500,
                  fontSize: '0.95rem',
                }}
              >
                No accounts match "{searchQuery}"
              </Typography>
            </Box>
          )}

          {/* Pagination */}
          <TablePagination
            component="div"
            count={pagination.total}
            page={pagination.page}
            onPageChange={(e, newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
            rowsPerPage={pagination.pageSize}
            onRowsPerPageChange={(e) => setPagination(prev => ({
              ...prev,
              pageSize: parseInt(e.target.value, 10),
              page: 0
            }))}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{
              borderTop: '1px solid #E5E5E5',
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontFamily: 'Metropolis, sans-serif',
              },
            }}
          />
          </>
        )}
      </Paper>
      </motion.div>

      {/* Create OU Dialog */}
      <Dialog open={createOUOpen} onClose={() => setCreateOUOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle sx={{ fontFamily: 'Bitter, Georgia, serif', fontWeight: 600 }}>Create OU</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
            Parent: <strong>{createOUParent?.name}</strong> ({createOUParent?.id})
          </Typography>
          <TextField
            autoFocus label="OU Name" value={newOUName}
            onChange={(e) => setNewOUName(e.target.value)} fullWidth size="small"
            onKeyDown={(e) => e.key === 'Enter' && handleCreateOU()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOUOpen(false)} disabled={orgActionLoading}>Cancel</Button>
          <Button onClick={handleCreateOU} variant="contained" disabled={orgActionLoading || !newOUName.trim()}
            sx={{ bgcolor: '#4698D3', '&:hover': { bgcolor: '#3580B8' } }}>
            {orgActionLoading ? <CircularProgress size={20} color="inherit" /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete OU Dialog */}
      <Dialog open={deleteOUOpen} onClose={() => setDeleteOUOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle sx={{ fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, color: '#E74C3C' }}>Delete OU</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete <strong>{deleteOUTarget?.name}</strong> ({deleteOUTarget?.id})?
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: '#999' }}>
            The OU must be empty (no accounts or child OUs).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOUOpen(false)} disabled={orgActionLoading}>Cancel</Button>
          <Button onClick={handleDeleteOU} variant="contained" disabled={orgActionLoading}
            sx={{ bgcolor: '#E74C3C', '&:hover': { bgcolor: '#C0392B' } }}>
            {orgActionLoading ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Move Account Dialog */}
      <Dialog open={moveOpen} onClose={() => setMoveOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle sx={{ fontFamily: 'Bitter, Georgia, serif', fontWeight: 600 }}>Move Account</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
            Account: <strong>{moveAccountNode?.name}</strong> ({moveAccountNode?.id})
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Destination OU</InputLabel>
            <Select value={moveDestParent} onChange={(e) => setMoveDestParent(e.target.value)} label="Destination OU">
              {allOUs.filter((ou) => ou.id !== moveSourceParent).map((ou) => (
                <MenuItem key={ou.id} value={ou.id}>{ou.name} ({ou.id})</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveOpen(false)} disabled={orgActionLoading}>Cancel</Button>
          <Button onClick={handleMoveAccount} variant="contained" disabled={orgActionLoading || !moveDestParent}
            sx={{ bgcolor: '#4698D3', '&:hover': { bgcolor: '#3580B8' } }}>
            {orgActionLoading ? <CircularProgress size={20} color="inherit" /> : 'Move'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Onboarding Modal */}
      <Dialog
        open={showOnboardingModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            border: '1px solid #E5E5E5',
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: '#FEF9E7',
            borderBottom: '1px solid #E5E5E5',
            fontFamily: 'Bitter, Georgia, serif',
            fontWeight: 600,
            fontSize: '1.25rem',
            color: '#253E66',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 2,
            px: 3,
            position: 'relative',
          }}
        >
          <Avatar sx={{ backgroundColor: '#F39C12', width: 40, height: 40 }}>
            <CloudIcon />
          </Avatar>
          {onboardingMethod === 'automated' ? 'Automated Onboarding' :
            onboardingMethod === 'manual' ? 'Manual Onboarding' :
              'Onboard AWS Account'}
          <IconButton
            onClick={handleCloseModal}
            size="small"
            sx={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#666666',
              backgroundColor: '#FFFFFF',
              border: '1px solid #E5E5E5',
              '&:hover': { backgroundColor: '#F9FBFD' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, backgroundColor: '#FFFFFF' }}>
          {loadingConfig ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress sx={{ color: '#F39C12' }} />
            </Box>
          ) : !onboardingMethod ? (
            /* Step 1: Choose onboarding method */
            <motion.div key="method-select" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <Box sx={{ p: 4 }}>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.875rem',
                  color: '#666666',
                  fontWeight: 500,
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Choose how you want to onboard your AWS account
              </Typography>

              <Grid container spacing={3}>
                {/* Automated Option */}
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: '1px solid #E5E5E5',
                      borderRadius: '12px',
                      transition: 'all 0.2s ease',
                      position: 'relative',
                      height: '100%',
                      '&:hover': {
                        borderColor: '#3498DB',
                        boxShadow: '0 4px 12px rgba(52, 152, 219, 0.15)',
                      },
                    }}
                    onClick={() => setOnboardingMethod('automated')}
                  >
                    <Chip
                      label="Recommended"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 12,
                        left: 12,
                        backgroundColor: '#253E66',
                        color: '#FFFFFF',
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                      }}
                    />
                    <CardContent sx={{ textAlign: 'center', pt: 5, pb: 3 }}>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          backgroundColor: '#EBF5FB',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto',
                          mb: 2,
                        }}
                      >
                        <SettingsIcon sx={{ fontSize: 40, color: '#3498DB' }} />
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: 'Bitter, Georgia, serif',
                          fontWeight: 600,
                          fontSize: '1.1rem',
                          color: '#253E66',
                          mb: 1,
                        }}
                      >
                        Automated
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: 'Metropolis, sans-serif',
                          fontSize: '0.85rem',
                          color: '#666666',
                          mb: 2,
                        }}
                      >
                        Run a CloudFormation template
                      </Typography>
                      <IconButton
                        sx={{
                          border: '1px solid #3498DB',
                          color: '#3498DB',
                          '&:hover': { backgroundColor: '#EBF5FB' },
                        }}
                      >
                        <ArrowForwardIcon />
                      </IconButton>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Manual Option */}
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: '1px solid #E5E5E5',
                      borderRadius: '12px',
                      transition: 'all 0.2s ease',
                      height: '100%',
                      '&:hover': {
                        borderColor: '#3498DB',
                        boxShadow: '0 4px 12px rgba(52, 152, 219, 0.15)',
                      },
                    }}
                    onClick={() => setOnboardingMethod('manual')}
                  >
                    <CardContent sx={{ textAlign: 'center', pt: 5, pb: 3 }}>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          backgroundColor: '#EBF5FB',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto',
                          mb: 2,
                        }}
                      >
                        <TouchAppIcon sx={{ fontSize: 40, color: '#3498DB' }} />
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: 'Bitter, Georgia, serif',
                          fontWeight: 600,
                          fontSize: '1.1rem',
                          color: '#253E66',
                          mb: 1,
                        }}
                      >
                        Manual
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: 'Metropolis, sans-serif',
                          fontSize: '0.85rem',
                          color: '#666666',
                          mb: 2,
                        }}
                      >
                        Follow step-by-step instructions
                      </Typography>
                      <IconButton
                        sx={{
                          border: '1px solid #3498DB',
                          color: '#3498DB',
                          '&:hover': { backgroundColor: '#EBF5FB' },
                        }}
                      >
                        <ArrowForwardIcon />
                      </IconButton>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
            </motion.div>
          ) : onboardingMethod === 'automated' ? (
            /* Automated Onboarding Flow */
            <motion.div key="automated-flow" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}>
            <Box sx={{ p: 3 }}>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.875rem',
                  color: '#666666',
                  fontWeight: 500,
                  mb: 3,
                }}
              >
                Run a CloudFormation template to onboard the AWS account
              </Typography>

              <Paper
                sx={{
                  p: 3,
                  backgroundColor: '#F9FBFD',
                  borderRadius: '8px',
                  border: '1px solid #E5E5E5',
                  mb: 3,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Avatar sx={{ backgroundColor: '#3498DB', width: 28, height: 28, fontSize: '0.875rem' }}>
                    1
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        fontSize: '0.9rem',
                        color: '#253E66',
                        fontWeight: 500,
                      }}
                    >
                      Log in to the AWS account and run this{' '}
                      <Link
                        href={getCloudFormationUrl()}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: '#E74C3C',
                          fontWeight: 600,
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        CloudFormation Template
                      </Link>
                      , click on <strong>Create Stack</strong>.
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.875rem',
                  color: '#666666',
                  fontWeight: 500,
                  mb: 2,
                }}
              >
                Enter the AWS account details
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  label="AWS Account ID"
                  value={accounts[0].accountId}
                  onChange={(e) => handleAccountChange(0, 'accountId', filterInput(e.target.value, 'NUMERIC'))}
                  placeholder="123456789012"
                  size="small"
                  required
                  error={accounts[0].accountId && !validateAccountId(accounts[0].accountId)}
                  helperText={accounts[0].accountId && !validateAccountId(accounts[0].accountId) ? 'Must be exactly 12 digits' : ' '}
                  sx={{ ...inputStyle, flex: 1 }}
                />
                <TextField
                  label="Account Name"
                  value={accounts[0].accountName}
                  onChange={(e) => handleAccountChange(0, 'accountName', filterInput(e.target.value, 'GENERAL_TEXT'))}
                  placeholder="Production Account"
                  size="small"
                  required
                  error={accounts[0].accountId && validateAccountId(accounts[0].accountId) && !accounts[0].accountName.trim()}
                  helperText={accounts[0].accountId && validateAccountId(accounts[0].accountId) && !accounts[0].accountName.trim() ? 'Account name is required' : ' '}
                  sx={{ ...inputStyle, flex: 1 }}
                />
              </Box>

              {/* Validation Section */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant={validated ? 'contained' : 'outlined'}
                  onClick={handleValidateAccount}
                  disabled={validating || !accounts[0].accountId || !validateAccountId(accounts[0].accountId) || !accounts[0].accountName.trim()}
                  startIcon={
                    validating ? (
                      <CircularProgress size={18} sx={{ color: validated ? 'white' : '#27AE60' }} />
                    ) : validated ? (
                      <CheckCircleIcon />
                    ) : null
                  }
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '6px',
                    borderColor: validated ? '#27AE60' : '#27AE60',
                    color: validated ? '#FFFFFF' : '#27AE60',
                    backgroundColor: validated ? '#27AE60' : 'transparent',
                    '&:hover': {
                      borderColor: '#219A52',
                      backgroundColor: validated ? '#219A52' : 'rgba(39, 174, 96, 0.08)',
                    },
                    '&.Mui-disabled': {
                      borderColor: '#E5E5E5',
                      color: '#999999',
                    },
                  }}
                >
                  {validating ? 'Validating...' : validated ? 'Validated' : 'Validate'}
                </Button>
                {validated && (
                  <Typography sx={{ color: '#27AE60', fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem' }}>
                    Access to AWS account verified
                  </Typography>
                )}
              </Box>

              {validationError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {validationError}
                </Alert>
              )}
            </Box>
            </motion.div>
          ) : (
            /* Manual Onboarding Flow */
            <motion.div key="manual-flow" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}>
            <Box sx={{ p: 3 }}>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.875rem',
                  color: '#666666',
                  fontWeight: 500,
                  mb: 3,
                }}
              >
                Follow these steps to manually create an IAM role in your AWS account
              </Typography>

              <Paper
                sx={{
                  p: 3,
                  backgroundColor: '#F9FBFD',
                  borderRadius: '8px',
                  border: '1px solid #E5E5E5',
                  mb: 3,
                }}
              >
                {/* Step 1 */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
                  <Avatar sx={{ backgroundColor: '#3498DB', width: 28, height: 28, fontSize: '0.875rem' }}>
                    1
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        fontSize: '0.9rem',
                        color: '#253E66',
                        fontWeight: 500,
                      }}
                    >
                      Create an{' '}
                      <Link
                        href="https://console.aws.amazon.com/iam/home#/roles/create"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: '#E74C3C',
                          fontWeight: 600,
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        IAM Role
                      </Link>
                      {' '}named{' '}
                      <Tooltip title={copiedRoleName ? 'Copied!' : 'Click to copy'} arrow>
                        <Box
                          component="span"
                          onClick={handleCopyRoleName}
                          sx={{
                            fontFamily: 'monospace',
                            backgroundColor: '#E5E5E5',
                            px: 1,
                            py: 0.5,
                            borderRadius: '4px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                            '&:hover': {
                              backgroundColor: '#D5D5D5',
                            },
                          }}
                        >
                          {onboardingConfig?.switchRoleName || 'ckprism-role'}
                        </Box>
                      </Tooltip>
                      {' '}in your AWS account.
                    </Typography>
                  </Box>
                </Box>

                {/* Step 2 */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
                  <Avatar sx={{ backgroundColor: '#3498DB', width: 28, height: 28, fontSize: '0.875rem' }}>
                    2
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        fontSize: '0.9rem',
                        color: '#253E66',
                        fontWeight: 500,
                        mb: 1,
                      }}
                    >
                      Attach the following AWS managed policies to the role:
                    </Typography>
                    <List dense sx={{ pl: 2 }}>
                      {[
                        'AWSSSODirectoryReadOnly',
                        'AWSSSOReadOnly',
                        'AWSOrganizationsReadOnlyAccess',
                        'IAMFullAccess'
                      ].map((policy) => (
                        <ListItem key={policy} sx={{ py: 0.25 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <CheckCircleIcon sx={{ fontSize: 16, color: '#27AE60' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={policy}
                            primaryTypographyProps={{
                              sx: { fontFamily: 'monospace', fontSize: '0.85rem', color: '#253E66' }
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Box>

                {/* Step 3 */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Avatar sx={{ backgroundColor: '#3498DB', width: 28, height: 28, fontSize: '0.875rem' }}>
                    3
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        fontSize: '0.9rem',
                        color: '#253E66',
                        fontWeight: 500,
                        mb: 1.5,
                      }}
                    >
                      Edit the role's trust policy to include the following:
                    </Typography>
                    <Paper
                      sx={{
                        p: 2,
                        backgroundColor: '#2C3E50',
                        borderRadius: '8px',
                        position: 'relative',
                      }}
                    >
                      <IconButton
                        onClick={handleCopyTrustPolicy}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          color: '#FFFFFF',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                        }}
                      >
                        <Tooltip title={copiedPolicy ? 'Copied!' : 'Copy to clipboard'}>
                          <ContentCopyIcon fontSize="small" />
                        </Tooltip>
                      </IconButton>
                      <Typography
                        component="pre"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          color: '#ECF0F1',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                          m: 0,
                          pr: 4,
                        }}
                      >
                        {getTrustPolicy()}
                      </Typography>
                    </Paper>
                  </Box>
                </Box>
              </Paper>

              <Divider sx={{ my: 3 }} />

              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.875rem',
                  color: '#666666',
                  fontWeight: 500,
                  mb: 2,
                }}
              >
                Enter the AWS account details
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  label="AWS Account ID"
                  value={accounts[0].accountId}
                  onChange={(e) => handleAccountChange(0, 'accountId', filterInput(e.target.value, 'NUMERIC'))}
                  placeholder="123456789012"
                  size="small"
                  required
                  error={accounts[0].accountId && !validateAccountId(accounts[0].accountId)}
                  helperText={accounts[0].accountId && !validateAccountId(accounts[0].accountId) ? 'Must be exactly 12 digits' : ' '}
                  sx={{ ...inputStyle, flex: 1 }}
                />
                <TextField
                  label="Account Name"
                  value={accounts[0].accountName}
                  onChange={(e) => handleAccountChange(0, 'accountName', filterInput(e.target.value, 'GENERAL_TEXT'))}
                  placeholder="Production Account"
                  size="small"
                  required
                  error={accounts[0].accountId && validateAccountId(accounts[0].accountId) && !accounts[0].accountName.trim()}
                  helperText={accounts[0].accountId && validateAccountId(accounts[0].accountId) && !accounts[0].accountName.trim() ? 'Account name is required' : ' '}
                  sx={{ ...inputStyle, flex: 1 }}
                />
              </Box>

              {/* Validation Section */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant={validated ? 'contained' : 'outlined'}
                  onClick={handleValidateAccount}
                  disabled={validating || !accounts[0].accountId || !validateAccountId(accounts[0].accountId) || !accounts[0].accountName.trim()}
                  startIcon={
                    validating ? (
                      <CircularProgress size={18} sx={{ color: validated ? 'white' : '#27AE60' }} />
                    ) : validated ? (
                      <CheckCircleIcon />
                    ) : null
                  }
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '6px',
                    borderColor: validated ? '#27AE60' : '#27AE60',
                    color: validated ? '#FFFFFF' : '#27AE60',
                    backgroundColor: validated ? '#27AE60' : 'transparent',
                    '&:hover': {
                      borderColor: '#219A52',
                      backgroundColor: validated ? '#219A52' : 'rgba(39, 174, 96, 0.08)',
                    },
                    '&.Mui-disabled': {
                      borderColor: '#E5E5E5',
                      color: '#999999',
                    },
                  }}
                >
                  {validating ? 'Validating...' : validated ? 'Validated' : 'Validate'}
                </Button>
                {validated && (
                  <Typography sx={{ color: '#27AE60', fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem' }}>
                    Access to AWS account verified
                  </Typography>
                )}
              </Box>

              {validationError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {validationError}
                </Alert>
              )}
            </Box>
            </motion.div>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            pt: 2,
            backgroundColor: '#FFFFFF',
            borderTop: '1px solid #E5E5E5',
            gap: 1.5,
            justifyContent: onboardingMethod ? 'space-between' : 'flex-end',
          }}
        >
          {onboardingMethod ? (
            <>
              <Button
                onClick={handleBack}
                disabled={loading}
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  textTransform: 'none',
                  color: '#666666',
                  borderColor: '#E5E5E5',
                  borderRadius: '6px',
                  '&:hover': {
                    borderColor: '#999999',
                    backgroundColor: '#F9FBFD',
                  },
                }}
                variant="outlined"
              >
                Back
              </Button>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                  onClick={handleCloseModal}
                  disabled={loading}
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    textTransform: 'none',
                    color: '#666666',
                    borderColor: '#E5E5E5',
                    borderRadius: '6px',
                    '&:hover': {
                      borderColor: '#999999',
                      backgroundColor: '#F9FBFD',
                    },
                  }}
                  variant="outlined"
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleOnboardingSubmit}
                  disabled={loading || !validated || !accounts[0].accountId || !validateAccountId(accounts[0].accountId)}
                  startIcon={loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <CloudIcon />}
                  sx={{
                    backgroundColor: '#F39C12',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '6px',
                    px: 3,
                    '&:hover': {
                      backgroundColor: '#E67E22',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#E5E5E5',
                      color: '#999999',
                    },
                  }}
                >
                  {loading ? 'Processing...' : 'Submit'}
                </Button>
              </Box>
            </>
          ) : (
            <Button
              onClick={handleCloseModal}
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                color: '#666666',
                borderColor: '#E5E5E5',
                borderRadius: '6px',
                '&:hover': {
                  borderColor: '#999999',
                  backgroundColor: '#F9FBFD',
                },
              }}
              variant="outlined"
            >
              Cancel
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Account Rename Modal */}
      <AccountRenameModal
        open={showRenameModal}
        onClose={() => setShowRenameModal(false)}
        account={renamingAccount}
        onSuccess={handleRenameSuccess}
      />

      {/* Account Owners Modal */}
      <AccountOwnersModal
        open={showOwnersModal}
        onClose={() => {
          setShowOwnersModal(false);
          setIsBulkOwnersMode(false);
        }}
        account={ownersAccount}
        onSuccess={handleOwnersSuccess}
        users={users}
        bulkAccountIds={isBulkOwnersMode ? selectedAccounts : []}
      />
    </motion.div>
  );
};

export default ManualOnboarding;
